/**
 * Sign Out Handler
 *
 * Clears all cached data when user signs out to prevent
 * data leakage between user sessions.
 */

import { useEffect } from 'react';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { clearAllQueries } from '../../utils/queryClient';
import { createLogger } from '../../utils/logger';

const log = createLogger('SignOutHandler');

export const SignOutHandler: React.FC = () => {
  const { isSignedIn, userId } = useAuth();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { signOut } = useClerk();

  useEffect(() => {
    // Store the previous sign-in state
    const previousSignInState = localStorage.getItem('tripgen_signed_in');
    const currentSignInState = isSignedIn ? 'true' : 'false';

    // Detect sign out (was signed in, now signed out)
    if (previousSignInState === 'true' && !isSignedIn) {
      log.info('User signed out - clearing all cached data');
      clearAllCachedData();
    }

    // Update the stored sign-in state
    localStorage.setItem('tripgen_signed_in', currentSignInState);
  }, [isSignedIn, userId]);

  return null;
};

/**
 * Clear all cached data including:
 * - React Query cache
 * - LocalStorage data
 * - Session storage
 */
export const clearAllCachedData = async () => {
  try {
    log.info('Clearing all cached data...');

    // Clear React Query cache
    await clearAllQueries();

    // Clear localStorage items (keep only essential config)
    const keysToKeep = [
      'tripgen_signed_in', // Track sign-in state
      'clerk-db-jwt', // Clerk auth data
    ];

    const allKeys = Object.keys(localStorage);
    allKeys.forEach((key) => {
      if (!keysToKeep.some((keepKey) => key.includes(keepKey))) {
        localStorage.removeItem(key);
        log.debug(`Removed localStorage key: ${key}`);
      }
    });

    // Clear session storage
    sessionStorage.clear();

    log.info('Successfully cleared all cached data');
  } catch (error) {
    log.error('Error clearing cached data', error);
  }
};

/**
 * Clear user-specific data when switching users
 * (less aggressive than full sign out)
 */
export const clearUserSpecificData = async (newUserId: string) => {
  try {
    log.info(`Switching to user ${newUserId} - clearing user-specific data`);

    // Clear React Query cache
    await clearAllQueries();

    // Clear planner state
    localStorage.removeItem('tripgen_planner_state');
    localStorage.removeItem('tripgen_nav_current_plan');

    log.info('Successfully cleared user-specific data');
  } catch (error) {
    log.error('Error clearing user-specific data', error);
  }
};
